import React, { useEffect, useRef, useState } from 'react';
import './RocketGame.css';

const RocketGame = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const gameStateRef = useRef({
    rocket: { x: 100, y: 200, width: 40, height: 30, velocityY: 0 },
    obstacles: [],
    score: 0,
    gameSpeed: 2.5,  // Réduit de 3 pour plus de contrôle
    isThrusting: false,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;
    let animationId;
    let obstacleTimer = 0;

    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !gameOver) {
        e.preventDefault();
        game.isThrusting = true;
      }
      if (e.code === 'Space' && gameOver) {
        e.preventDefault();
        resetGame();
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        game.isThrusting = false;
      }
    };

    const handleMouseDown = (e) => {
      if (!gameOver) {
        game.isThrusting = true;
      } else {
        resetGame();
      }
    };

    const handleMouseUp = () => {
      game.isThrusting = false;
    };

    const resetGame = () => {
      game.rocket = { x: 100, y: 200, width: 40, height: 30, velocityY: 0 };
      game.obstacles = [];
      game.score = 0;
      game.gameSpeed = 2.5;  // Cohérent avec la valeur initiale
      game.isThrusting = false;
      setScore(0);
      setGameOver(false);
    };

    const checkCollision = (rocket, obstacle) => {
      return (
        rocket.x < obstacle.x + obstacle.width &&
        rocket.x + rocket.width > obstacle.x &&
        rocket.y < obstacle.y + obstacle.height &&
        rocket.y + rocket.height > obstacle.y
      );
    };

    const gameLoop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Fond étoilé
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Étoiles
      for (let i = 0; i < 50; i++) {
        ctx.fillStyle = 'white';
        ctx.fillRect((game.score * 2 + i * 15) % canvas.width, (i * 37) % canvas.height, 2, 2);
      }

      // Physique de la fusée (paramètres ajustés pour meilleure jouabilité)
      if (game.isThrusting) {
        game.rocket.velocityY -= 0.25;  // Réduit encore - montée très douce
      } else {
        game.rocket.velocityY += 0.2;   // Réduit de 0.3 - descente encore plus douce
      }

      game.rocket.velocityY = Math.max(-5, Math.min(5, game.rocket.velocityY));  // Limites réduites de ±8 à ±5
      game.rocket.y += game.rocket.velocityY;

      // Vérifier les limites
      if (game.rocket.y < 0 || game.rocket.y + game.rocket.height > canvas.height) {
        setGameOver(true);
      }

      // Dessiner la fusée
      ctx.fillStyle = '#667eea';
      ctx.beginPath();
      ctx.moveTo(game.rocket.x + game.rocket.width, game.rocket.y + game.rocket.height / 2);
      ctx.lineTo(game.rocket.x, game.rocket.y);
      ctx.lineTo(game.rocket.x, game.rocket.y + game.rocket.height);
      ctx.closePath();
      ctx.fill();

      // Flammes
      if (game.isThrusting) {
        ctx.fillStyle = '#ff6b6b';
        ctx.beginPath();
        ctx.moveTo(game.rocket.x, game.rocket.y + 5);
        ctx.lineTo(game.rocket.x - 15, game.rocket.y + game.rocket.height / 2);
        ctx.lineTo(game.rocket.x, game.rocket.y + game.rocket.height - 5);
        ctx.closePath();
        ctx.fill();
      }

      // Générer des obstacles
      obstacleTimer++;
      if (obstacleTimer > 80) {
        const gap = 150;
        const topHeight = Math.random() * (canvas.height - gap - 100) + 50;
        
        game.obstacles.push({
          x: canvas.width,
          topHeight: topHeight,
          bottomY: topHeight + gap,
          width: 30,
        });
        obstacleTimer = 0;
      }

      // Mettre à jour et dessiner les obstacles
      game.obstacles.forEach((obstacle, index) => {
        obstacle.x -= game.gameSpeed;

        // Dessiner obstacle du haut
        ctx.fillStyle = '#e74c3c';
        ctx.fillRect(obstacle.x, 0, obstacle.width, obstacle.topHeight);
        
        // Dessiner obstacle du bas
        ctx.fillRect(obstacle.x, obstacle.bottomY, obstacle.width, canvas.height - obstacle.bottomY);

        // Vérifier collision
        const topObstacle = {
          x: obstacle.x,
          y: 0,
          width: obstacle.width,
          height: obstacle.topHeight,
        };
        
        const bottomObstacle = {
          x: obstacle.x,
          y: obstacle.bottomY,
          width: obstacle.width,
          height: canvas.height - obstacle.bottomY,
        };

        if (checkCollision(game.rocket, topObstacle) || checkCollision(game.rocket, bottomObstacle)) {
          setGameOver(true);
        }

        // Supprimer et scorer
        if (obstacle.x + obstacle.width < 0) {
          game.obstacles.splice(index, 1);
          game.score += 10;
          setScore(game.score);
          
          if (game.score % 50 === 0 && game.gameSpeed < 7) {
            game.gameSpeed += 0.3;
          }
        }
      });

      // Afficher le score
      ctx.fillStyle = 'white';
      ctx.font = 'bold 20px Arial';
      ctx.fillText(`Distance: ${game.score}`, 10, 30);

      if (!gameOver) {
        animationId = requestAnimationFrame(gameLoop);
      } else {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'white';
        ctx.font = 'bold 30px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('GAME OVER', canvas.width / 2, canvas.height / 2 - 20);
        ctx.font = '20px Arial';
        ctx.fillText(`Distance: ${game.score}`, canvas.width / 2, canvas.height / 2 + 20);
        ctx.font = '16px Arial';
        ctx.fillText('Cliquez ou ESPACE pour rejouer', canvas.width / 2, canvas.height / 2 + 60);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    
    gameLoop();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      cancelAnimationFrame(animationId);
    };
  }, [gameOver]);

  return (
    <div className="game-container">
      <h3>🚀 Course Generator Rocket</h3>
      <p className="game-instructions">Maintenez ESPACE ou CLIQUEZ pour monter, relâchez pour descendre</p>
      <canvas ref={canvasRef} width={900} height={600} className="game-canvas" />
      <div className="game-score">Distance: {score}</div>
    </div>
  );
};

export default RocketGame;















